import { useEffect, useState } from "react";
import { ListSearchSort } from "@/components/ListSearchSort";
import { sortByName, matchesQuery } from "@/lib/listSort";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSport, SPORT_META } from "@/context/SportContext";
import { OfferingFilters, matchesDate } from "@/components/OfferingFilters";
import SportSwitcher from "@/components/SportSwitcher";
import ConflictModal from "@/components/ConflictModal";
import { CalendarDays, MapPin, Users, Plus, Trash2, Lock, Sparkles, Copy, Mail, Pencil, UserCheck, Zap, Package as PackageIcon, Info } from "lucide-react";
import RallyCourtButton from "@/components/RallyCourtButton";
import { toast } from "sonner";
import { buildAvatarUrl } from "@/components/ProfileImageUpload";
import { CancelEnrolment } from "@/components/CancelEnrolment";
import { AssessmentAckDialog } from "@/components/AssessmentAckDialog";
import { editLockReason } from "@/pages/OfferingNew";

export default function Offerings() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const isCoach = user?.role === "coach" || user?.role === "admin";
  const { sport, meta } = useSport();
  const [filters, setFilters] = useState({ coach: "", date: "", day: "", sport: "", level: "" });
  const [offQuery, setOffQuery] = useState("");
  const [offSort, setOffSort] = useState("default");
  const [items, setItems] = useState([]);
  const [myPackages, setMyPackages] = useState([]);
  const [conflicts, setConflicts] = useState(null);
  const [pendingEnrol, setPendingEnrol] = useState(null); // { id, price }
  const [assessmentAck, setAssessmentAck] = useState(null); // { message, retry }
  const [assessmentOnly, setAssessmentOnly] = useState(() => {
    const qs = new URLSearchParams(window.location.search);
    // Iter94b — accept both legacy `?assessment=1` and the new
    // `?assessment_only=true` deep-link so the credit-gate redirect works.
    return qs.get("assessment") === "1" || qs.get("assessment_only") === "true";
  });
  // Iter74 Phase B — pathway deep-link. When the student clicks a Helter
  // Skelter medal / Athlete Card zone, we route them here with ?focus=<axis>
  // and filter the offerings list to only those whose title / description /
  // skill_focus tags mention that axis. A clear-focus pill lets them widen
  // the list back to everything.
  const [focusFilter, setFocusFilter] = useState(() =>
    new URLSearchParams(window.location.search).get("focus") || ""
  );
  const [regions, setRegions] = useState([]);
  const [regionFilter, setRegionFilter] = useState(() => {
    // Persist the region filter across page loads so a student visiting
    // /offerings picks up wherever they left off — and defaults to their
    // home region the very first time.
    return localStorage.getItem("rl_region_filter") || user?.home_region_id || "";
  });
  // Iter87 — "View enrolled" modal state. Fetches lazily.
  const [enrolledModal, setEnrolledModal] = useState(null);

  // Iter92 — calendar deep-links land on /offerings#offering-{id}. Scroll to
  // the card + brief volt-yellow ring so it's obvious which one was clicked.
  const [highlightId, setHighlightId] = useState(null);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const hash = window.location.hash;
    if (!hash.startsWith("#offering-")) return;
    const id = hash.slice("#offering-".length);
    setHighlightId(id);
    const t = setTimeout(() => {
      const el = document.getElementById(`offering-${id}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 400);
    const clear = setTimeout(() => setHighlightId(null), 3200);
    return () => { clearTimeout(t); clearTimeout(clear); };
  }, [items]);


  // Iter94 — Package Store state. `tab` toggles between "sessions" (default
  // calendar view) and "packages" (bundle store). `credits` holds the student's
  // live credit balance so we can decorate credit-eligible session cards.
  // `rl_offerings_tab` is a soft deep-link — the StudentDashboard credits card
  // sets it before navigating so the Packages tab is already open on arrival.
  const [tab, setTab] = useState(() => {
    try {
      const t = localStorage.getItem("rl_offerings_tab");
      if (t) { localStorage.removeItem("rl_offerings_tab"); return t; }
    } catch (_e) { /* silent */ }
    return "sessions";
  });
  const [packages, setPackages] = useState([]);
  const [credits, setCredits] = useState({ total_remaining: 0, next_expiry: null, packs: [] });

  // Refresh user so assessment_status updates after enrol/checkout
  const refreshUser = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (_e) { /* ignore */ }
  };

  const load = async () => {
    const qs = new URLSearchParams();
    if (filters.sport) qs.set("sport", filters.sport);
    if (regionFilter) qs.set("region_id", regionFilter);
    const { data } = await api.get(`/offerings?${qs.toString()}`);
    setItems(data);
    // For students, fetch active packages so we can show "Redeem" CTA on
    // covered Performance offerings.
    if (!isCoach && user?.role === "student") {
      try {
        const { data: pks } = await api.get("/packages/mine");
        setMyPackages(pks || []);
      } catch (_e) { /* ignore */ }
      // Iter94 — live credit balance for the "Use 1 Credit" button state.
      try {
        const { data: c } = await api.get("/me/credits");
        setCredits(c);
      } catch (_e) { /* silent */ }
    }
    // Iter94 — Store tab. Always fetch (coaches see it too so they can preview
    // their own bundle catalogue). Sport filter is intentionally NOT applied
    // to the store view — packages can be sport-agnostic bundles.
    try {
      const { data: pkgs } = await api.get(`/offerings?packages_only=true`);
      setPackages(pkgs || []);
    } catch (_e) { setPackages([]); }
  };

  useEffect(() => {
    api.get("/regions?active=true").then((r) => setRegions(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => {
    localStorage.setItem("rl_region_filter", regionFilter || "");
  }, [regionFilter]);
  useEffect(() => { load();   }, [sport, regionFilter, filters.sport]);

  // After Stripe checkout success, poll for payment status
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    if (!sid) return;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sid}`);
        if (data.payment_status === "paid") {
          toast.success("Payment confirmed — you're enrolled");
          window.history.replaceState({}, "", "/offerings");
          await refreshUser();
          load();
          return;
        }
        if (data.status === "expired") {
          toast.error("Checkout expired");
          window.history.replaceState({}, "", "/offerings");
          return;
        }
        if (attempts < 6) setTimeout(poll, 2000);
      } catch (e) {
        if (attempts < 4) setTimeout(poll, 2500);
      }
    };
    toast.message("Checking payment…");
    poll();
     
  }, []);

  // Assessment-pending gate: returns true when the error was handled here.
  const handleAssessmentGate = (detail, retry) => {
    if (!detail || typeof detail !== "object") return false;
    if (detail.code === "assessment_ack_required") { setAssessmentAck({ message: detail.message, retry }); return true; }
    if (detail.code === "before_assessment") { toast.error(detail.message); return true; }
    if (detail.code === "assessment_required") {
      toast.error(detail.message || "Book an assessment first");
      setTimeout(() => nav(detail.redirect_to || "/offerings?assessment_only=true"), 900);
      return true;
    }
    return false;
  };

  const enrol = async (id, price, ack = false) => {
    try {
      if (Number(price) > 0) {
        const { data } = await api.post("/checkout/offering", {
          offering_id: id,
          origin_url: window.location.origin,
          assessment_ack: ack,
        });
        if (data.url) { window.location.href = data.url; return; }
      }
      await api.post(`/offerings/${id}/enrol`, null, { params: ack ? { assessment_ack: true } : {} });
      toast.success("You're enrolled");
      await refreshUser();
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (handleAssessmentGate(detail, () => enrol(id, price, true))) return;
      toast.error(formatApiError(detail) || "Failed");
    }
  };

  // Iter94 — Credit-book. Only surfaced on offerings that opt in via
  // `accept_credits` AND the student has ≥1 credit remaining. Backend
  // handles the FIFO consumption + assessment gate.
  const bookWithCredit = async (id, ack = false) => {
    try {
      const { data } = await api.post(`/offerings/${id}/use-credit`, null, { params: ack ? { assessment_ack: true } : {} });
      toast.success(`Credit spent — ${data.credits_remaining} left`);
      await refreshUser();
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      // Iter94b — assessment-gate structured error. Backend returns
      // `{code:'assessment_required', message, redirect_to}` so we can toast
      // + jump the student straight to the assessment slot picker.
      if (handleAssessmentGate(detail, () => bookWithCredit(id, true))) return;
      toast.error(formatApiError(detail) || "Couldn't spend credit");
    }
  };

  // Iter94 — Package Store purchase. Kicks off Stripe checkout and redirects.
  // Iter94b — Optional promo code path. `codeByPackage` holds the input value
  // per-card; `previewByPackage` caches the price preview from /promo-codes/apply
  // so students see the discount before hitting Buy.
  const [codeByPackage, setCodeByPackage] = useState({});
  const [previewByPackage, setPreviewByPackage] = useState({});
  const setCodeFor = (pkgId, v) => setCodeByPackage((prev) => ({ ...prev, [pkgId]: v }));
  const applyPromo = async (pkgId) => {
    const code = (codeByPackage[pkgId] || "").trim();
    if (!code) return;
    try {
      const { data } = await api.post("/promo-codes/apply", { code, offering_id: pkgId });
      setPreviewByPackage((prev) => ({ ...prev, [pkgId]: data }));
      toast.success(`${data.code} → save £${data.discount_amount}`);
    } catch (e) {
      setPreviewByPackage((prev) => ({ ...prev, [pkgId]: null }));
      toast.error(formatApiError(e.response?.data?.detail) || "Code not accepted");
    }
  };
  const clearPromo = (pkgId) => {
    setCodeByPackage((prev) => ({ ...prev, [pkgId]: "" }));
    setPreviewByPackage((prev) => ({ ...prev, [pkgId]: null }));
  };
  const buyPackage = async (offering_id) => {
    try {
      const promo = (codeByPackage[offering_id] || "").trim() || undefined;
      const { data } = await api.post("/checkout/package-store", {
        offering_id,
        origin_url: window.location.origin,
        promo_code: promo,
      });
      if (data.url) { window.location.href = data.url; return; }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't start checkout"); }
  };

  // Check if an offering is covered by an active student package.
  // Performance offerings: any active pack with same coach+sport.
  // Assessment offerings: only packs flagged `covers_assessment=true`.
  const packageCovering = (off) => {
    if (off.is_assessment) {
      return myPackages.find(
        (p) => p.coach_id === off.coach_id && p.sport === off.sport && p.sessions_remaining > 0 && p.covers_assessment,
      ) || null;
    }
    if (off.session_type !== "performance") return null;
    return myPackages.find(
      (p) => p.coach_id === off.coach_id && p.sport === off.sport && p.sessions_remaining > 0,
    ) || null;
  };

  const redeemPackage = async (id, ack = false) => {
    try {
      const { data } = await api.post(`/packages/redeem/${id}`, null, { params: ack ? { assessment_ack: true } : {} });
      toast.success(`Enrolled · ${data.sessions_remaining} session${data.sessions_remaining === 1 ? "" : "s"} left in pack`);
      await refreshUser();
      load();
    } catch (e) {
      // Iter168 — cutoff-reached comes back as a structured object with
      // {code, message, coach_id, hours_until_start}. Show a targeted
      // toast that hands the player their coach id so they can contact
      // them directly instead of a confusing generic error.
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "cutoff_reached") {
        toast.error(detail.message || "Bookings close 24h before session start — contact your coach.", {
          action: detail.coach_id ? {
            label: "Contact coach",
            onClick: () => { window.location.href = `/coaches/${detail.coach_id}#contact`; },
          } : undefined,
          duration: 10000,
        });
        return;
      }
      if (handleAssessmentGate(detail, () => redeemPackage(id, true))) return;
      toast.error(formatApiError(detail) || "Failed to redeem");
    }
  };

  const handleEnrolClick = async (offering) => {
    if (isCoach) return;
    try {
      const { data } = await api.post("/bookings/check-conflict", {
        start: offering.start,
        duration_min: offering.duration_min,
      });
      const hard = (data.hard || []).length > 0;
      const soft = (data.soft || []).length > 0;
      if (hard || soft) {
        setPendingEnrol({ id: offering.id, price: offering.price_per_player });
        setConflicts(data);
        return;
      }
      await enrol(offering.id, offering.price_per_player);
    } catch (e) {
      // If conflict check fails, fall through to direct enrol
      await enrol(offering.id, offering.price_per_player);
    }
  };

  const confirmAfterConflict = async () => {
    if (!pendingEnrol) return;
    const { id, price } = pendingEnrol;
    setConflicts(null);
    setPendingEnrol(null);
    await enrol(id, price);
  };

  const unenrol = async (id) => {
    try {
      await api.delete(`/offerings/${id}/enrol`);
      toast.success("Removed");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete offering?")) return;
    try {
      await api.delete(`/offerings/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const duplicateOffering = async (off) => {
    const src = new Date(off.start);
    const next = new Date(src.getTime() + 7 * 24 * 60 * 60 * 1000);
    const pad = (n) => String(n).padStart(2, "0");
    const defaultIso = `${next.getFullYear()}-${pad(next.getMonth() + 1)}-${pad(next.getDate())}T${pad(next.getHours())}:${pad(next.getMinutes())}`;
    const input = window.prompt(
      `Duplicate "${off.title}" — when should the new session run?\nEnter date & time as YYYY-MM-DDTHH:MM (24h)`,
      defaultIso,
    );
    if (!input) return;
    const start = new Date(input);
    if (isNaN(start.getTime())) { toast.error("Invalid date/time"); return; }
    try {
      await api.post(`/offerings/${off.id}/duplicate`, { start: start.toISOString() });
      toast.success(`Duplicated to ${start.toLocaleString()}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not duplicate"); }
  };

  // Iter87 — inline quick-edit via prompts. Full modal-form editing goes
  // through /offerings/new?edit_id=… (existing form, extended below). This
  // handler is kept lightweight for the common cases coaches ask about most:
  // moving the start time and changing max spaces / price.
  const editOffering = (off) => {
    // Delegate to the fully-featured form page so all fields are editable.
    // Persist scroll position so returning drops us back on the same card.
    try { sessionStorage.setItem("rl_offering_return", window.scrollY); } catch (_e) { /* ignore */ }
    window.location.href = `/offerings/new?edit_id=${off.id}`;
  };

  const openEnrolled = async (off) => {
    setEnrolledModal({ offering: off, students: null });
    try {
      const { data } = await api.get(`/offerings/${off.id}/enrolled`);
      setEnrolledModal({ offering: off, students: Array.isArray(data) ? data : [] });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load enrolments");
      setEnrolledModal(null);
    }
  };

  const assessmentRequired = !isCoach && user?.role === "student" && user?.assessment_status !== "completed";
  const assessmentScheduled = user?.assessment_status === "scheduled";
  const profileIncomplete = !isCoach && user?.role === "student" && user?.needs_personal_details === true;

  // Iter78: Structured skill_focus tag matching (with fuzzy fallback).
  // Offerings have a `skill_focus: string[]` picked by the coach. If the
  // ?focus= param matches any tag (case-insensitive), we treat it as an
  // exact structured match. Otherwise we fall back to the old fuzzy hay
  // search across title / description so offerings without tags still
  // filter usefully.
  const focusMatches = (o) => {
    if (!focusFilter) return true;
    const needle = focusFilter.toLowerCase();
    const tags = (o.skill_focus || []).map((t) => (t || "").toLowerCase());
    if (tags.some((t) => t === needle || t.includes(needle))) return true;
    const hay = [o.title, o.description].filter(Boolean).join(" ").toLowerCase();
    return hay.includes(needle);
  };
  const visibleItems = sortByName((assessmentOnly ? items.filter((o) => o.is_assessment) : items)
    .filter(focusMatches)
    .filter((o) => (!filters.coach || o.coach_id === filters.coach)
      && (!filters.level || o.skill_level === filters.level)
      && matchesDate(o, filters.date, filters.day))
    .filter((o) => matchesQuery(offQuery, o.title, o.coach_name, o.venue, o.location)), (o) => o.title, offSort);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4 fade-up">
        <div>
          <div className="label-mono mb-2">OFFERINGS</div>
          <h1 className="h-display text-5xl tracking-tighter">Open sessions.</h1>
          <div className="text-sm text-[#A3A3A3] mt-1">
            {isCoach ? "Published group sessions students can enrol in." : "Group sessions you can join."}
          </div>
        </div>
        <div className="flex gap-3 items-center">
          <SportSwitcher />
          {isCoach && (
            <Link to="/offerings/new" className="btn-volt px-5 py-3 text-xs inline-flex items-center gap-2" data-testid="new-offering-link">
              <Plus className="w-4 h-4" /> New offering
            </Link>
          )}
        </div>
      </div>

      {/* Iter94 — Sessions vs Packages tabs. Packages tab lists all bundle
          offerings the student can buy — Stripe checkout on click. Coaches
          also see the tab so they can preview / QA their own store listing. */}
      <div className="flex items-center gap-1 border-b border-[#262626]" data-testid="offerings-tabs">
        <button
          type="button"
          onClick={() => setTab("sessions")}
          data-testid="tab-sessions"
          className="px-4 py-2.5 text-xs uppercase tracking-wider transition-colors"
          style={tab === "sessions"
            ? { color: "#CCFF00", borderBottom: "2px solid #CCFF00", marginBottom: "-1px", fontWeight: 700 }
            : { color: "#A3A3A3" }}
        >
          <span className="inline-flex items-center gap-2"><CalendarDays className="w-3.5 h-3.5" /> Sessions</span>
        </button>
        <button
          type="button"
          onClick={() => setTab("packages")}
          data-testid="tab-packages"
          className="px-4 py-2.5 text-xs uppercase tracking-wider transition-colors"
          style={tab === "packages"
            ? { color: "#CCFF00", borderBottom: "2px solid #CCFF00", marginBottom: "-1px", fontWeight: 700 }
            : { color: "#A3A3A3" }}
        >
          <span className="inline-flex items-center gap-2">
            <PackageIcon className="w-3.5 h-3.5" /> Packages
            {packages.length > 0 && <span className="text-[9px] text-[#525252]">({packages.length})</span>}
          </span>
        </button>
        {/* Live credit balance chip for students so they always see what they can spend. */}
        {!isCoach && user?.role === "student" && Number(credits.total_remaining || 0) > 0 && (
          <div className="ml-auto label-mono px-3 py-1.5 text-[#CCFF00] flex items-center gap-1.5" data-testid="offerings-credits-chip">
            <Zap className="w-3 h-3" /> {credits.total_remaining} credit{credits.total_remaining === 1 ? "" : "s"}
          </div>
        )}
      </div>

      {/* Iter74 Phase B — focus filter banner (pathway deep-link). Sticky
          pill at the top of the offerings list so the student can see how
          they arrived here and clear the filter with one click. */}
      {focusFilter && tab === "sessions" && (
        <div
          className="card-surface p-3 sm:p-4 flex items-center gap-3 flex-wrap fade-up border-l-2 border-[#CCFF00]"
          data-testid="offerings-focus-banner"
        >
          <div className="label-mono text-[10px] text-[#CCFF00]">FOCUSED BY YOUR PATHWAY</div>
          <div className="text-sm">
            Showing sessions that work on <strong>{focusFilter}</strong>. Not what you wanted?
          </div>
          <button
            onClick={() => {
              setFocusFilter("");
              const url = new URL(window.location.href);
              url.searchParams.delete("focus");
              window.history.replaceState({}, "", url.toString());
            }}
            data-testid="offerings-focus-clear"
            className="text-[10px] uppercase tracking-wider text-[#A3A3A3] hover:text-[#CCFF00] ml-auto"
          >
            Show all offerings
          </button>
        </div>
      )}

      {/* Region filter pill — drives the offerings list and persists in localStorage.
          Defaults to the student's home region; the "Looking elsewhere?" button
          jumps back to all-regions for vacation / travel cases. */}
      {(
        <div className="card-surface p-3 sm:p-4 flex items-center gap-3 flex-wrap fade-up" data-testid="offerings-region-bar">
          {regions.length > 0 && (<>
          <div className="label-mono text-[10px]">REGION</div>
          <select
            value={regionFilter}
            onChange={(e) => setRegionFilter(e.target.value)}
            className="bg-[#0A0A0A] border border-[#262626] px-3 py-1.5 text-sm focus:border-[#CCFF00] focus:outline-none"
            data-testid="region-select"
          >
            <option value="">All regions</option>
            {regions.map((r) => (
              <option key={r.id} value={r.id}>{r.label}</option>
            ))}
          </select>
          {regionFilter && (
            <button
              onClick={() => setRegionFilter("")}
              className="text-[10px] uppercase tracking-wider text-[#A3A3A3] hover:text-[#CCFF00]"
              data-testid="region-clear"
              title="Looking elsewhere — show every region (good for vacations / travel)"
            >
              Looking elsewhere? Show all
            </button>
          )}
          </>)}
          <OfferingFilters items={items} f={filters} setF={setFilters} />
          <div className="text-[10px] text-[#A3A3A3] ml-auto" data-testid="offerings-count">{visibleItems.length} session{visibleItems.length === 1 ? "" : "s"}</div>
          <div className="w-full"><ListSearchSort query={offQuery} onQuery={setOffQuery} sort={offSort} onSort={setOffSort} modes={["default", "az", "za"]} placeholder="Search sessions by title, coach or venue…" testid="offerings" /></div>
        </div>
      )}

      {/* Personal-details gate banner — appears for any student who hasn't completed their profile */}
      {profileIncomplete && (  <div className="card-surface p-5 border-l-4 border-[#CCFF00] flex items-start gap-4 fade-up" data-testid="profile-incomplete-banner">
          <div className="w-10 h-10 bg-[#CCFF00] flex items-center justify-center shrink-0">
            <Lock strokeWidth={2.5} className="w-5 h-5 text-black" />
          </div>
          <div className="flex-1">
            <div className="label-mono mb-1" style={{ color: "#CCFF00" }}>PROFILE INCOMPLETE</div>
            <h3 className="h-display text-2xl tracking-tight">Add your contact &amp; address details before your first booking.</h3>
            <p className="text-sm text-[#A3A3A3] mt-1">We need this for insurance and session communications. Coaches don&apos;t see it — only Rally Lab admins do.</p>
            <Link to="/profile/personal?return=/offerings" data-testid="profile-incomplete-cta" className="btn-volt mt-3 px-4 py-2 text-xs inline-flex items-center gap-2">
              Complete details now
            </Link>
          </div>
        </div>
      )}

      {/* Assessment gate banner — for students who haven't completed it */}
      {assessmentRequired && (
        <div
          data-testid="assessment-required-banner"
          className="card-surface p-5 border-l-4 border-[#FF4500] flex items-start gap-4 fade-up">
          <div className="w-10 h-10 bg-[#FF4500] flex items-center justify-center shrink-0">
            <Lock strokeWidth={2.5} className="w-5 h-5 text-black" />
          </div>
          <div className="flex-1">
            <div className="label-mono mb-1" style={{ color: "#FF4500" }}>
              {assessmentScheduled ? "ASSESSMENT BOOKED" : "ASSESSMENT REQUIRED"}
            </div>
            <h3 className="h-display text-2xl tracking-tight">
              {assessmentScheduled
                ? "You're booked in. You can already book sessions scheduled after your assessment."
                : "Book your first 1:1 assessment to unlock Performance & 1:1 sessions."}
            </h3>
            <p className="text-sm text-[#A3A3A3] mt-1">
              A short on-court physical review with a coach — price set per coach (some include it in a coaching package). Once it's booked you can book other sessions scheduled after it.
            </p>
            {!assessmentOnly && (
              <button
                onClick={() => setAssessmentOnly(true)}
                data-testid="show-assessment-only"
                className="mt-3 px-4 py-2 text-xs uppercase tracking-wider border border-[#FF4500] text-[#FF4500] hover:bg-[#FF4500] hover:text-black transition-colors">
                Show assessment slots
              </button>
            )}
          </div>
        </div>
      )}

      {/* Filter toggle for assessment-only view */}
      {tab === "sessions" && !isCoach && (assessmentOnly || items.some((o) => o.is_assessment)) && (
        <div className="flex gap-2 fade-up">
          <button
            onClick={() => setAssessmentOnly(false)}
            data-testid="filter-all"
            className="px-3 py-1.5 text-xs uppercase tracking-wider border transition-colors"
            style={!assessmentOnly ? { background: "#CCFF00", color: "#0A0A0A", borderColor: "#CCFF00", fontWeight: 700 } : { borderColor: "#262626", color: "#A3A3A3" }}>
            All sessions
          </button>
          <button
            onClick={() => setAssessmentOnly(true)}
            data-testid="filter-assessment"
            className="px-3 py-1.5 text-xs uppercase tracking-wider border transition-colors"
            style={assessmentOnly ? { background: "#FF4500", color: "#0A0A0A", borderColor: "#FF4500", fontWeight: 700 } : { borderColor: "#262626", color: "#A3A3A3" }}>
            Assessment only
          </button>
        </div>
      )}

      {tab === "sessions" && !visibleItems.length && (
        <div className="card-surface p-12 text-center fade-up d1">
          <CalendarDays className="w-12 h-12 text-[#262626] mx-auto mb-4" strokeWidth={1.5} />
          <div className="h-display text-2xl mb-1">No {meta.label.toLowerCase()} offerings yet</div>
          <div className="text-sm text-[#A3A3A3]">{isCoach ? "Create your first one." : (assessmentOnly ? "No assessment slots published yet. Ask your coach to schedule one." : "Check back soon, or submit a session request.")}</div>
        </div>
      )}

      {tab === "sessions" && (
      <div className="grid md:grid-cols-2 gap-4 fade-up d1" data-testid="sessions-grid">
        {visibleItems.map((o) => {
          const enrolled = o.enrolled_student_ids?.includes(user?.id);
          const full = o.spaces_left <= 0;
          const d = new Date(o.start);
          const m = SPORT_META[o.sport] || meta;
          const isAssessment = !!o.is_assessment;
          // Coach credentials gate — backend marks coach_bookable=false when their cert/insurance
          // has lapsed (super_admin can override per-coach). Already-enrolled students still see
          // their booking; new bookings are blocked.
          const coachBlocked = o.coach_bookable === false;
          const gated = !isCoach
            && !isAssessment
            && user?.role === "student"
            && user?.assessment_status !== "completed"
            && !(user?.assessment_booking_start
              && new Date(o.start) >= new Date(new Date(user.assessment_booking_start).getTime() + (user.assessment_booking_duration || 60) * 60000))
            && !enrolled;
          return (
            <div key={o.id}
              id={`offering-${o.id}`}
              className={`card-surface p-6 ${isAssessment ? "ring-1 ring-[#FF4500]/50" : ""} ${coachBlocked && !enrolled ? "opacity-60" : ""} ${highlightId === o.id ? "ring-2 ring-[#CCFF00] shadow-[0_0_40px_rgba(204,255,0,0.25)]" : ""} transition-all`}
              data-testid={`offering-${o.id}`}>
              {coachBlocked && !isCoach && (
                <div className="mb-3 flex items-start gap-2 px-3 py-2 border-l-2 border-[#FF4500] bg-[#1a0a05] text-xs" data-testid={`coach-blocked-${o.id}`}>
                  <Lock className="w-3.5 h-3.5 text-[#FF4500] mt-0.5 shrink-0" />
                  <span className="text-[#FFD3C2]">Coach credentials lapsed — new bookings paused. Check back soon.</span>
                </div>
              )}
              {o.coach_override_active && isCoach && (
                <div className="mb-3 flex items-start gap-2 px-3 py-2 border-l-2 border-[#CCFF00] bg-[#0a1a05] text-xs" data-testid={`coach-override-${o.id}`}>
                  <span className="text-[#CCFF00] font-bold">OVERRIDE ACTIVE</span>
                  <span className="text-[#A3A3A3]">— admin granted booking access while credentials are renewed</span>
                </div>
              )}
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className="label-mono" style={{ color: m.color }}>{o.sport.toUpperCase()}</span>
                    <span className="w-1 h-1 bg-[#A3A3A3]" />
                    <span className="label-mono">{o.skill_level}</span>
                    <span className="w-1 h-1 bg-[#A3A3A3]" />
                    <span className="label-mono">{o.session_type.replace("_"," ")}</span>
                    {o.tailored && <span className="label-mono text-[#CCFF00]">· TAILORED</span>}
                    {isAssessment && (
                      <span className="label-mono px-1.5 py-0.5 bg-[#FF4500] text-black font-bold" data-testid="assessment-badge">· ASSESSMENT</span>
                    )}
                  </div>
                  <h3 className="h-display text-2xl tracking-tight leading-tight">{o.title}</h3>
                </div>
                <div className="text-right">
                  <div className="label-mono">£</div>
                  <div className="h-display text-3xl" style={{ color: isAssessment ? "#FF4500" : m.color }}>{o.price_per_player}</div>
                  <div className="label-mono text-[0.6rem]">PER PLAYER</div>
                </div>
              </div>
              <div className="space-y-2 text-sm mb-4">
                <div className="flex items-center gap-2 text-[#E5E5E5]">
                  <CalendarDays className="w-3.5 h-3.5 text-[#A3A3A3]" />
                  {d.toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })} · {o.duration_min} min
                </div>
                <div className="flex items-center gap-2 text-[#E5E5E5]">
                  <MapPin className="w-3.5 h-3.5 text-[#A3A3A3]" /> {o.venue}
                </div>
                {/* Iter105 — Multi-region tags. Show every region the offering
                    is tagged with so students in overlap catchment areas
                    (e.g. NW London / M25 West) can see it belongs to both.
                    Iter106 — Gate on at least one resolved region so we don't
                    render an empty flex row when region_ids point at stale
                    or archived regions. */}
                {(() => {
                  const tags = (o.region_ids || [])
                    .map((rid) => regions.find((x) => x.id === rid))
                    .filter(Boolean);
                  if (tags.length === 0) return null;
                  return (
                    <div className="flex flex-wrap items-center gap-1.5 pl-5" data-testid={`offering-regions-${o.id}`}>
                      {tags.map((r) => (
                        <span key={r.id} className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 border border-[#262626] text-[#A3A3A3]">{r.label}</span>
                      ))}
                    </div>
                  );
                })()}
                <div className="flex items-center gap-2 text-[#E5E5E5]">
                  <Users className="w-3.5 h-3.5 text-[#A3A3A3]" />
                  {o.enrolled_student_ids?.length || 0} / {o.max_spaces} enrolled · <span style={{ color: full ? "#FF4500" : m.color }}>{full ? "Full" : `${o.spaces_left} space${o.spaces_left===1?"":"s"} left`}</span>
                  {/* Iter143 — surface the min-players floor so students know why
                      the "Rally the Court" nudge exists. */}
                  {o.min_players && (o.enrolled_student_ids?.length || 0) < Number(o.min_players) && (
                    <span
                      className="ml-2 px-1.5 py-0.5 text-[10px] uppercase tracking-wider border border-[#FF9F1C] text-[#FF9F1C]"
                      data-testid={`min-players-need-${o.id}`}
                      title={`Coach set a minimum of ${o.min_players} players`}
                    >
                      Needs {Number(o.min_players) - (o.enrolled_student_ids?.length || 0)} more
                    </span>
                  )}
                </div>
              </div>
              {o.skill_focus?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {o.skill_focus.map((s) => (
                    <span key={s} className="px-2 py-0.5 text-[10px] uppercase tracking-wider border border-[#262626]">{s}</span>
                  ))}
                </div>
              )}
              {/* Iter87: `flex-wrap` prevents the 4th+ button being pushed
                  off-screen on narrow iPhones. `min-h-[44px]` hits Apple's
                  minimum tap-target on every action button below. */}
              <div className="flex flex-wrap gap-2">
                {/* Iter143 — Rally the Court. Booked players (and the coach)
                    see this whenever a min_players floor is set but not yet
                    met. Server-side rate-limited to 1 rally per user per
                    session per 6h. */}
                {(enrolled || isCoach)
                  && !isAssessment
                  && !o.is_package
                  && Number(o.min_players || 0) > 0
                  && (o.enrolled_student_ids?.length || 0) < Number(o.min_players)
                  && !full && (
                  <RallyCourtButton offering={o} />
                )}
                {!isCoach && (
                  enrolled && o.my_booking_id ? (
                    <CancelEnrolment bookingId={o.my_booking_id} paidByCredit={o.my_payment_status === "credit"} onCancelled={load} testid={`unenrol-${o.id}`} />
                  ) : enrolled ? (
                    <button type="button" onClick={() => unenrol(o.id)} className="min-h-[44px] px-4 py-2.5 text-xs uppercase tracking-wider border border-[#262626] hover:border-[#FF4500] hover:text-[#FF4500] active:scale-95 transition-all" data-testid={`unenrol-${o.id}`}>Cancel enrolment</button>
                  ) : coachBlocked ? (
                    <div className="flex items-center gap-2 text-xs text-[#A3A3A3]" data-testid={`coach-blocked-cta-${o.id}`}>
                      <Lock className="w-3.5 h-3.5 text-[#FF4500]" />
                      Bookings paused — credentials renewing
                    </div>
                  ) : gated ? (
                    <div className="flex items-center gap-2 text-xs text-[#A3A3A3]" data-testid={`locked-${o.id}`}>
                      <Lock className="w-3.5 h-3.5 text-[#FF4500]" />
                      {user?.assessment_booking_start
                        ? `Bookable for sessions after your assessment (${new Date(user.assessment_booking_start).toLocaleDateString("en-GB", { day: "numeric", month: "short" })})`
                        : "Complete your assessment to unlock"}
                    </div>
                  ) : (() => {
                    const pkg = packageCovering(o);
                    if (pkg) {
                      return (
                        <button
                          disabled={full}
                          onClick={() => redeemPackage(o.id)}
                          className="btn-volt px-4 py-2 text-xs disabled:opacity-30 inline-flex items-center gap-2"
                          style={{ background: "#CCFF00", color: "#0A0A0A" }}
                          data-testid={`redeem-${o.id}`}
                        >
                          {full ? "Session full" : `Use Performance pack · ${pkg.sessions_remaining} left`}
                        </button>
                      );
                    }
                    // Iter79: pending players CAN browse but can't book real
                    // sessions yet — only the assessment offering is unlocked
                    // so they can complete their entry ritual.
                    const pendingApproval = !isCoach && user?.player_status === "pending" && !isAssessment;
                    if (pendingApproval) {
                      return (
                        <button
                          disabled
                          className="px-4 py-2 text-xs opacity-50 border border-[#262626] inline-flex items-center gap-2"
                          data-testid={`enrol-locked-${o.id}`}
                          title="Your account is under review. You'll be able to book once approved."
                        >
                          Booking locked · under review
                        </button>
                      );
                    }
                    // Iter94 — "Use 1 Credit" button when the offering opts
                    // into credit spending and the student has a balance.
                    // Higher priority than paying but lower than an already-
                    // owned Performance pack (which is coach-specific).
                    const canUseCredit = !isCoach && !pendingApproval && !full
                      && o.accept_credits && Number(credits.total_remaining || 0) > 0;
                    if (canUseCredit) {
                      // Iter112 — Show the "Pay instead" secondary link when
                      // the coach opted into student choice on this offering.
                      // Wrapped in a small flex column so the primary CTA
                      // keeps its size but the fallback sits underneath.
                      const showCashFallback = o.allow_pay_when_credit_available !== false && Number(o.price_per_player) > 0;
                      return (
                        <div className="flex flex-col items-start gap-1">
                          <button
                            onClick={() => bookWithCredit(o.id)}
                            className="btn-volt px-4 py-2 text-xs inline-flex items-center gap-2"
                            style={{ background: "#CCFF00", color: "#0A0A0A" }}
                            data-testid={`use-credit-${o.id}`}
                          >
                            <Zap className="w-3.5 h-3.5" /> Use 1 Credit
                          </button>
                          {showCashFallback && (
                            <button
                              type="button"
                              onClick={() => handleEnrolClick(o)}
                              data-testid={`pay-instead-${o.id}`}
                              className="text-[10px] uppercase tracking-wider text-[#A3A3A3] hover:text-white transition-colors underline underline-offset-2"
                              title="Save your credit for later and pay for this one"
                            >
                              Pay £{o.price_per_player} instead →
                            </button>
                          )}
                        </div>
                      );
                    }
                    return (
                      <button disabled={full} onClick={() => handleEnrolClick(o)}
                        className="btn-volt px-4 py-2 text-xs disabled:opacity-30 inline-flex items-center gap-2"
                        style={{ background: isAssessment ? "#FF4500" : m.color, color: "#0A0A0A" }}
                        data-testid={`enrol-${o.id}`}>
                        {full ? "Session full" : (Number(o.price_per_player) > 0
                          ? (isAssessment ? `Book assessment · £${o.price_per_player}` : `Pay £${o.price_per_player} & enrol`)
                          : "Enrol free")}
                      </button>
                    );
                  })()
                )}
                {isCoach && (
                  <>
                    {editLockReason(o) ? (
                      <button type="button" onClick={() => { window.location.href = `/offerings/new?copy_id=${o.id}`; }} title={editLockReason(o)} className="min-h-[44px] px-3 py-2.5 text-xs text-[#CCFF00] hover:opacity-70 active:scale-95 transition-all inline-flex items-center gap-1" data-testid={`copy-as-new-${o.id}`}>
                        <Copy className="w-3.5 h-3.5" /> Copy as new session
                      </button>
                    ) : (
                      <button type="button" onClick={() => editOffering(o)} className="min-h-[44px] px-3 py-2.5 text-xs text-[#CCFF00] hover:opacity-70 active:scale-95 active:opacity-50 transition-all inline-flex items-center gap-1" data-testid={`edit-offering-${o.id}`}>
                        <Pencil className="w-3.5 h-3.5" /> Edit
                      </button>
                    )}
                    <button type="button" onClick={() => duplicateOffering(o)} className="min-h-[44px] px-3 py-2.5 text-xs text-[#CCFF00] hover:opacity-70 active:scale-95 active:opacity-50 transition-all inline-flex items-center gap-1" data-testid={`dup-offering-${o.id}`}>
                      <Copy className="w-3.5 h-3.5" /> Duplicate
                    </button>
                    <button type="button" onClick={() => openEnrolled(o)} className="min-h-[44px] px-3 py-2.5 text-xs text-[#A3A3A3] hover:text-white active:scale-95 active:opacity-50 transition-all inline-flex items-center gap-1" data-testid={`view-enrolled-${o.id}`}>
                      <UserCheck className="w-3.5 h-3.5" /> View enrolled ({o.enrolled_student_ids?.length || 0})
                    </button>
                    <Link to={`/offerings/${o.id}`} className="min-h-[44px] px-3 py-2.5 text-xs text-[#CCFF00] hover:opacity-70 inline-flex items-center gap-1" data-testid={`open-session-${o.id}`}>
                      Open session
                    </Link>
                    {(o.enrolled_student_ids?.length || 0) > 0 && (
                      <Link
                        to={`/admin/emails?offering_id=${o.id}`}
                        className="min-h-[44px] px-3 py-2.5 text-xs text-[#FF4500] hover:opacity-70 active:scale-95 active:opacity-50 transition-all inline-flex items-center gap-1"
                        data-testid={`message-attendees-${o.id}`}
                        title="Blanket email everyone enrolled on this session"
                      >
                        <Mail className="w-3.5 h-3.5" /> Message attendees
                      </Link>
                    )}
                    {!(o.start && new Date(o.start) < new Date() && (o.enrolled_student_ids?.length || 0) > 0) && <button type="button" onClick={() => remove(o.id)} className="min-h-[44px] px-3 py-2.5 text-xs text-[#FF4500] hover:opacity-70 active:scale-95 active:opacity-50 transition-all inline-flex items-center gap-1" data-testid={`del-offering-${o.id}`}>
                      <Trash2 className="w-3.5 h-3.5" /> Delete
                    </button>}
                    {/* Iter145 — Under-filled sessions surface a one-tap
                        "Cancel & refund" so coaches don't have to hunt for
                        the delete button after the min-players bell fires.
                        Refunds credits, marks stripe rows for refund, and
                        pings every enrolled player via bell + email. */}
                    {Number(o.min_players || 0) > 0
                      && o.min_players_check_fired
                      && (o.enrolled_student_ids?.length || 0) < Number(o.min_players)
                      && !o.is_package && !(o.start && new Date(o.start) < new Date()) && (
                      <button
                        type="button"
                        data-testid={`cancel-refund-${o.id}`}
                        onClick={() => {
                          const enrolled = o.enrolled_student_ids?.length || 0;
                          const ok = window.confirm(
                            `ARE YOU SURE? Cancel "${o.title || 'this session'}" and refund all ${enrolled} enrolled player${enrolled === 1 ? '' : 's'}?\n\n` +
                            "Credit-paid attendees get their credit back. Card-paid attendees are queued for refund (5-10 working days). You'll have 30 seconds to undo."
                          );
                          if (!ok) return;
                          // Iter147 — 30 s undo window. We DELAY the API call
                          // instead of trying to reverse it post-hoc, so an
                          // accidental tap can be walked back cleanly.
                          // Iter148 — Live countdown badge in the toast. We
                          // re-mount the toast every second with the same
                          // id so sonner updates the description in place.
                          const startedAt = Date.now();
                          let cancelled = false;
                          const toastId = `cancel-refund-${o.id}`;
                          const enrolledCount = o.enrolled_student_ids?.length || 0;
                          const renderToast = (secondsLeft) => {
                            toast(`Cancelling "${o.title || 'session'}" in ${secondsLeft}s…`, {
                              id: toastId,
                              duration: 32_000,
                              description: `${enrolledCount} player${enrolledCount === 1 ? "" : "s"} will be refunded · tap Undo to keep the session live`,
                              action: {
                                label: "Undo",
                                onClick: () => {
                                  cancelled = true;
                                  clearTimeout(t);
                                  clearInterval(ticker);
                                  const secs = Math.round((Date.now() - startedAt) / 1000);
                                  toast.dismiss(toastId);
                                  toast.success(`Cancel undone (${secs}s in) — session stays live.`);
                                },
                              },
                            });
                          };
                          renderToast(30);
                          const ticker = setInterval(() => {
                            if (cancelled) return;
                            const secs = Math.max(0, 30 - Math.round((Date.now() - startedAt) / 1000));
                            renderToast(secs);
                            if (secs === 0) clearInterval(ticker);
                          }, 1000);
                          const t = setTimeout(async () => {
                            clearInterval(ticker);
                            if (cancelled) return;
                            try {
                              const { data } = await api.post(`/offerings/${o.id}/cancel-refund`);
                              toast.dismiss(toastId);
                              toast.success(
                                `Cancelled — notified ${data?.enrolled_notified || 0} player${(data?.enrolled_notified || 0) === 1 ? "" : "s"}.`
                              );
                              load();
                            } catch (e) {
                              toast.dismiss(toastId);
                              toast.error(formatApiError(e.response?.data?.detail) || "Could not cancel");
                            }
                          }, 30_000);
                        }}
                        className="min-h-[44px] px-3 py-2.5 text-xs text-[#FF4500] font-bold hover:opacity-70 active:scale-95 transition-all inline-flex items-center gap-1"
                        title="Under-filled — cancel and refund everyone in one tap (30s undo window)"
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Cancel &amp; refund
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
      )}

      {/* Iter94 — Packages / Bundle Store. Time-independent offerings. Coach
          sees "Edit" / "Delete"; student sees "Buy £X". Zero-price bundles
          are rejected server-side so we never render a broken CTA. */}
      {tab === "packages" && (
        <div className="grid md:grid-cols-2 gap-4 fade-up d1" data-testid="packages-grid">
          {packages.length === 0 && (
            <div className="card-surface p-12 text-center col-span-full">
              <PackageIcon className="w-12 h-12 text-[#262626] mx-auto mb-4" strokeWidth={1.5} />
              <div className="h-display text-2xl mb-1">No packages listed yet</div>
              <div className="text-sm text-[#A3A3A3]">
                {isCoach ? "Publish a bundle from New offering → Package to fill this store." : "Your coach hasn't published any bundles yet — check back soon."}
              </div>
            </div>
          )}
          {packages.map((p) => {
            const mine = isCoach && p.coach_id === user?.id;
            const currency = "£";
            return (
              <div key={p.id} className="card-surface p-5 space-y-3" data-testid={`package-card-${p.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="label-mono text-[#CCFF00] flex items-center gap-1"><PackageIcon className="w-3 h-3" /> PACKAGE</div>
                    <div className="h-display text-2xl mt-1 truncate">{p.title}</div>
                    <div className="text-xs text-[#A3A3A3] mt-1">{p.coach_name} · {p.sport}</div>
                    {/* Iter97 — Coach-only visual indicator when a bundle is
                        ring-fenced from promo codes. Kept in the header so
                        it's obvious in the grid at a glance. */}
                    {isCoach && p.allow_promo_codes === false && (
                      <div className="label-mono text-[#FF4500] mt-2 inline-flex items-center gap-1" data-testid={`promo-locked-badge-${p.id}`}>
                        <Lock className="w-3 h-3" /> NO DISCOUNTS
                      </div>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <div className="h-display text-3xl">{currency}{p.price_per_player}</div>
                    <div className="text-[10px] uppercase tracking-wider text-[#A3A3A3]">bundle price</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 pt-1">
                  <div className="border border-[#262626] p-2.5">
                    <div className="label-mono text-[#A3A3A3]">SESSIONS</div>
                    <div className="text-white text-xl font-bold mt-0.5">{p.session_count}</div>
                  </div>
                  <div className="border border-[#262626] p-2.5">
                    <div className="label-mono text-[#A3A3A3]">EXPIRES</div>
                    <div className="text-white text-xl font-bold mt-0.5">{p.expiry_months} mo</div>
                  </div>
                </div>
                {p.notes && <div className="text-xs text-[#A3A3A3]">{p.notes}</div>}
                {/* Iter94b — Promo code input. Only shown to students; coach
                    preview is intentionally read-only (they'd use the admin
                    manager instead). Preview response caches locally so students
                    see the discount before Stripe redirect. */}
                {!isCoach && user?.role === "student" && (() => {
                  // Iter97 — Hide the promo input on packages that have been
                  // ring-fenced. `allow_promo_codes` is treated as opt-in True
                  // for legacy docs (undefined ≠ false), so old bundles keep
                  // showing the input as before.
                  if (p.allow_promo_codes === false) {
                    return (
                      <div className="border border-[#262626] p-2.5 text-[11px] text-[#A3A3A3] italic" data-testid={`promo-locked-${p.id}`}>
                        No discount codes on this pack.
                      </div>
                    );
                  }
                  const preview = previewByPackage[p.id];
                  return (
                    <div className="border border-[#262626] p-2.5 space-y-2" data-testid={`promo-block-${p.id}`}>
                      <div className="flex items-center gap-2">
                        <input
                          value={codeByPackage[p.id] || ""}
                          onChange={(e) => setCodeFor(p.id, e.target.value.toUpperCase())}
                          placeholder="Promo code"
                          maxLength={32}
                          className="flex-1 bg-[#0A0A0A] border border-[#262626] px-2.5 py-1.5 text-xs text-white font-mono focus:border-[#CCFF00] focus:outline-none"
                          data-testid={`promo-input-${p.id}`}
                        />
                        {!preview && (
                          <button
                            type="button"
                            onClick={() => applyPromo(p.id)}
                            disabled={!(codeByPackage[p.id] || "").trim()}
                            className="px-2.5 py-1.5 text-xs border border-[#CCFF00] text-[#CCFF00] hover:bg-[#CCFF00] hover:text-black disabled:opacity-40"
                            data-testid={`promo-apply-${p.id}`}
                          >
                            Apply
                          </button>
                        )}
                        {preview && (
                          <button
                            type="button"
                            onClick={() => clearPromo(p.id)}
                            className="px-2.5 py-1.5 text-xs border border-[#525252] text-[#A3A3A3] hover:border-white hover:text-white"
                            data-testid={`promo-clear-${p.id}`}
                          >
                            Clear
                          </button>
                        )}
                      </div>
                      {/* Iter98c — Ask-your-club nudge. Inline one-liner is
                          always shown until the student has applied a code,
                          then hidden to avoid clutter. Curious users can tap
                          the ⓘ for a longer explainer. */}
                      {!preview && <PromoHint packageId={p.id} />}
                      {preview && (
                        <div className="text-[11px] text-[#CCFF00] flex items-center gap-2" data-testid={`promo-preview-${p.id}`}>
                          <span className="font-mono">{preview.code}</span>
                          <span className="text-[#A3A3A3]">— save £{preview.discount_amount}</span>
                          <span className="text-[#A3A3A3]">·</span>
                          <span>you pay £{preview.final_price}</span>
                        </div>
                      )}
                    </div>
                  );
                })()}
                <div className="flex items-center gap-2 pt-1">
                  {!isCoach && user?.role === "student" && (
                    <button
                      onClick={() => buyPackage(p.id)}
                      data-testid={`buy-package-${p.id}`}
                      className="btn-volt bg-[#CCFF00] text-black px-4 py-2 text-xs font-semibold hover:brightness-95 inline-flex items-center gap-2"
                    >
                      <Zap className="w-3.5 h-3.5" /> Buy · {currency}{previewByPackage[p.id]?.final_price ?? p.price_per_player}
                    </button>
                  )}
                  {mine && (
                    <>
                      <button
                        onClick={() => editOffering(p)}
                        className="text-xs text-[#CCFF00] hover:opacity-70 inline-flex items-center gap-1"
                        data-testid={`edit-package-${p.id}`}
                      >
                        <Pencil className="w-3.5 h-3.5" /> Edit
                      </button>
                      <button
                        onClick={() => remove(p.id)}
                        className="text-xs text-[#FF4500] hover:opacity-70 inline-flex items-center gap-1 ml-auto"
                        data-testid={`del-package-${p.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Delete
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <AssessmentAckDialog ack={assessmentAck} onClose={() => setAssessmentAck(null)} />
      <ConflictModal conflicts={conflicts} onCancel={() => { setConflicts(null); setPendingEnrol(null); }} onProceed={confirmAfterConflict} />
      <EnrolledModal state={enrolledModal} onClose={() => setEnrolledModal(null)} onChanged={load} />
    </div>
  );
}

function EnrolledModal({ state, onClose, onChanged }) {
  const [busy, setBusy] = useState({}); // studentId -> boolean
  if (!state) return null;
  const { offering, students } = state;
  const total = offering.enrolled_student_ids?.length || 0;

  const boot = async (student) => {
    if (!window.confirm(`Remove ${student.name} from "${offering.title}"? Their booking will be cancelled and they'll get a notification.`)) return;
    setBusy((s) => ({ ...s, [student.id]: true }));
    try {
      await api.delete(`/offerings/${offering.id}/enrolled/${student.id}`);
      toast.success(`${student.name} removed`);
      if (onChanged) onChanged(); // parent reloads offerings
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't unenrol");
    } finally {
      setBusy((s) => { const n = { ...s }; delete n[student.id]; return n; });
    }
  };

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
      data-testid="enrolled-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md card-surface p-5 border border-[#262626] max-h-[80vh] overflow-y-auto"
      >
        <div className="flex items-start justify-between mb-4 gap-3">
          <div>
            <div className="label-mono text-[#A3A3A3]">ENROLLED PLAYERS · {total}</div>
            <h3 className="h-display text-xl tracking-tight mt-1">{offering.title}</h3>
            <div className="text-[10px] uppercase tracking-wider text-[#525252] mt-1">
              {new Date(offering.start).toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
            </div>
          </div>
          <button
            type="button" onClick={onClose}
            className="min-h-[44px] min-w-[44px] px-3 text-xs uppercase tracking-wider border border-[#262626] hover:border-white active:scale-95 transition-all"
            data-testid="enrolled-modal-close"
          >
            Close
          </button>
        </div>
        {students === null ? (
          <div className="label-mono text-[#525252]">Loading…</div>
        ) : students.length === 0 ? (
          <div className="text-sm text-[#A3A3A3] py-6 text-center">No one has enrolled yet.</div>
        ) : (
          <div className="space-y-2">
            {students.map((s) => {
              const url = buildAvatarUrl(s.avatar_path);
              const isBusy = !!busy[s.id];
              return (
                <div key={s.id} className="border border-[#262626] p-3 flex items-center gap-3" data-testid={`enrolled-row-${s.id}`}>
                  <div className="w-10 h-10 rounded-full overflow-hidden border border-[#262626] bg-[#141414] flex items-center justify-center shrink-0">
                    {url
                      ? <img src={url} alt={s.name} className="w-full h-full object-cover" />
                      : <span className="text-[10px] uppercase text-[#525252]">{(s.name || "?").slice(0,2)}</span>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{s.name}</div>
                    <div className="text-[10px] text-[#A3A3A3] truncate">{s.email}</div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Link
                      to={`/admin/emails?student_id=${s.id}`}
                      className="min-h-[36px] min-w-[36px] text-[10px] uppercase tracking-wider px-2 py-1 border border-[#262626] hover:border-[#CCFF00] hover:text-[#CCFF00] active:scale-95 transition-all inline-flex items-center justify-center"
                      data-testid={`enrolled-message-${s.id}`}
                      title="Message this student"
                    >
                      <Mail className="w-3.5 h-3.5" />
                    </Link>
                    <Link
                      to={`/admin/users/${s.id}`}
                      className="min-h-[36px] text-[10px] uppercase tracking-wider px-2 py-1 border border-[#262626] hover:border-[#CCFF00] hover:text-[#CCFF00] active:scale-95 transition-all"
                      data-testid={`enrolled-view-${s.id}`}
                    >
                      View
                    </Link>
                    <button
                      type="button"
                      disabled={isBusy}
                      onClick={() => boot(s)}
                      className="min-h-[36px] min-w-[36px] text-[10px] uppercase tracking-wider px-2 py-1 border border-[#262626] text-[#FF4500] hover:border-[#FF4500] hover:bg-[#FF4500] hover:text-black active:scale-95 transition-all inline-flex items-center justify-center disabled:opacity-40"
                      data-testid={`enrolled-unenrol-${s.id}`}
                      title="Remove this student (unenrol)"
                    >
                      {isBusy ? "…" : <Trash2 className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Iter98c — PromoHint: inline "ask your club" nudge + ⓘ tooltip.
// Kept minimal on purpose so the promo block doesn't grow past two lines on
// mobile. The tooltip is click-to-toggle (mobile friendly) and closes on any
// outside tap via the document click handler.
// ---------------------------------------------------------------------------
function PromoHint({ packageId }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    // Defer to next tick so the click that opened it doesn't immediately close it.
    const id = setTimeout(() => document.addEventListener("click", close), 0);
    return () => { clearTimeout(id); document.removeEventListener("click", close); };
  }, [open]);
  return (
    <div className="flex items-start gap-1.5 text-[10.5px] text-[#A3A3A3] leading-snug relative" data-testid={`promo-hint-${packageId}`}>
      <span className="text-[#CCFF00] shrink-0">💡</span>
      <span className="flex-1">
        No code? <span className="text-white">Ask your club committee or coach</span> — they can share member-only rates.
      </span>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((s) => !s); }}
        className="text-[#525252] hover:text-white shrink-0 p-0.5 -m-0.5"
        aria-label="More info about promo codes"
        data-testid={`promo-hint-info-${packageId}`}
      >
        <Info className="w-3 h-3" />
      </button>
      {open && (
        <div
          className="absolute right-0 top-5 z-20 w-64 bg-[#0A0A0A] border border-[#CCFF00] p-3 text-[11px] text-[#A3A3A3] leading-relaxed shadow-lg"
          onClick={(e) => e.stopPropagation()}
          data-testid={`promo-hint-popover-${packageId}`}
        >
          <div className="text-white font-semibold mb-1">Where do codes come from?</div>
          Codes are usually issued by your <span className="text-white">club committee</span> or your <span className="text-white">coach</span>.
          They&apos;re member-only, one-time-use per student, and only one code applies per checkout — codes don&apos;t stack.
          If you don&apos;t have one, drop your rep a message before you buy.
        </div>
      )}
    </div>
  );
}
